/**
 * CardTile — several results together under one title (a trip: the fare,
 * the trains, what to pack).
 *
 * data: { sections: ({ type: <leaf kind>; heading? } & that kind's fields)[] }
 *
 * Each section is drawn by the same body as a tile of that kind, so a
 * table in a card and a table tile are one component. A checklist in a
 * card is still tickable: the change is written back into its section.
 */
import { FallbackTile } from "./FallbackTile";
import { LEAF_BODIES, type LeafKind } from "./leaves";
import type { TileBodyProps } from "../registry";

type Section = { type: string; heading?: string } & Record<string, unknown>;

export function CardTile({ data, w, h, onChange }: TileBodyProps) {
  const sections = ((data as { sections?: Section[] }).sections ?? []).filter(
    (s) => s && typeof s === "object",
  );
  if (!sections.length) return <p className="text-[12.5px] text-fg-faint">Empty card.</p>;

  return (
    <div className="flex flex-col divide-y divide-border">
      {sections.map((section, i) => {
        const { type, heading, ...fields } = section;
        const Body = LEAF_BODIES[type as LeafKind] ?? FallbackTile;
        const write = onChange
          ? (next: Record<string, unknown>) =>
              onChange({
                ...data,
                sections: sections.map((s, j) =>
                  j === i ? { type, ...(heading ? { heading } : {}), ...next } : s,
                ),
              })
          : undefined;
        return (
          <section key={i} className="py-2 first:pt-0 last:pb-0">
            {heading && (
              <h4 className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint">
                {heading}
              </h4>
            )}
            {/* Bounded height for the kinds that fill their box (chart,
                image, video); the rest size to their content. */}
            <div className={type === "chart" || type === "image" || type === "video" ? "h-[180px]" : ""}>
              <Body data={fields} w={w} h={h} onChange={write} />
            </div>
          </section>
        );
      })}
    </div>
  );
}
